import { ReactNode, useId, useMemo } from 'react';
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  createFilterOptions,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { SxProps, Theme } from '@mui/material/styles';
import { TransactionCategory, TransactionType } from '@inversor-hub/types';
import { transactionCategoryLabels } from './constants';

type CategoryScope = 'matching-type' | 'all';

interface TransactionCategoryGroup {
  id: string;
  type: TransactionType;
  label: string;
  description: string;
  categories: TransactionCategory[];
}

interface CategoryOption {
  category: TransactionCategory;
  label: string;
  description: string;
  groupId: string;
  groupLabel: string;
  type: TransactionType;
  keywords: string;
}

interface TransactionCategorySelectProps {
  value?: TransactionCategory | '';
  onChange: (category: TransactionCategory | '') => void;
  type?: TransactionType;
  preferredType?: TransactionType;
  scope?: CategoryScope;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  includeAllOption?: boolean;
  allOptionLabel?: string;
  hideLabel?: boolean;
  displayEmpty?: boolean;
  sx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
}

const CATEGORY_GROUPS: TransactionCategoryGroup[] = [
  {
    id: 'rental-income',
    type: 'INCOME',
    label: 'Ingresos del alquiler',
    description: 'Rentas, recuperaciones y ajustes con el inquilino.',
    categories: ['RENTAL_INCOME', 'TENANT_RECHARGES_INCOME', 'DEPOSIT_WITHHELD'],
  },
  {
    id: 'sale-income',
    type: 'INCOME',
    label: 'Venta y otros ingresos',
    description: 'Movimientos fuera del alquiler recurrente.',
    categories: ['SALE_INCOME', 'OTHER_INCOME'],
  },
  {
    id: 'financing',
    type: 'EXPENSE',
    label: 'Financiación',
    description: 'Distingue intereses deducibles de devolución de capital.',
    categories: ['FINANCING_INTEREST', 'LOAN_PRINCIPAL'],
  },
  {
    id: 'rental-operations',
    type: 'EXPENSE',
    label: 'Operativa del alquiler',
    description: 'Gastos recurrentes del inmueble arrendado.',
    categories: ['COMMUNITY_FEES', 'UTILITIES', 'INSURANCE', 'RENTAL_MANAGEMENT', 'CLEANING'],
  },
  {
    id: 'tax-and-services',
    type: 'EXPENSE',
    label: 'Fiscal y profesionales',
    description: 'Tributos, asesoría y defensa jurídica.',
    categories: ['LOCAL_TAXES', 'PROFESSIONAL_SERVICES', 'LEGAL_DEFENSE'],
  },
  {
    id: 'works',
    type: 'EXPENSE',
    label: 'Obras e inmueble',
    description: 'Separa conservación de mejoras que requieren revisión.',
    categories: ['REPAIRS_MAINTENANCE', 'IMPROVEMENT'],
  },
  {
    id: 'accounting',
    type: 'EXPENSE',
    label: 'Impagos y amortización',
    description: 'Partidas fiscales que conviene documentar bien.',
    categories: ['BAD_DEBT', 'DEPRECIATION_PROPERTY', 'DEPRECIATION_FURNITURE'],
  },
  {
    id: 'review',
    type: 'EXPENSE',
    label: 'Compra, venta y revisión',
    description: 'Casos que no entran limpio en el alquiler mensual.',
    categories: [
      'ACQUISITION_COSTS',
      'SALE_COSTS',
      'OTHER_DEDUCTIBLE_EXPENSE',
      'NON_DEDUCTIBLE_EXPENSE',
      'REVIEW',
    ],
  },
];

const CATEGORY_DESCRIPTIONS: Record<TransactionCategory, string> = {
  RENTAL_INCOME: 'Renta mensual o cobros directos de alquiler.',
  TENANT_RECHARGES_INCOME: 'Gastos cobrados al inquilino: suministros, IBI, comunidad.',
  DEPOSIT_WITHHELD: 'Fianza aplicada a rentas, daños o importes pendientes.',
  SALE_INCOME: 'Precio de venta o entrada vinculada a la transmisión.',
  OTHER_INCOME: 'Ingreso relacionado que no encaja en los anteriores.',
  FINANCING_INTEREST: 'Intereses, comisiones y gastos financieros identificados.',
  LOAN_PRINCIPAL: 'Amortización de capital del préstamo, no gasto deducible.',
  REPAIRS_MAINTENANCE: 'Averías, conservación y reparaciones para mantener el uso.',
  IMPROVEMENT: 'Reformas o mejoras que aumentan valor, vida útil o superficie.',
  LOCAL_TAXES: 'IBI, basuras, vados y otros tributos municipales.',
  COMMUNITY_FEES: 'Cuotas ordinarias, derramas y comunidad de propietarios.',
  UTILITIES: 'Luz, agua, gas, internet u otros suministros.',
  INSURANCE: 'Seguro de hogar, impago, responsabilidad civil u otros.',
  PROFESSIONAL_SERVICES: 'Gestoría, asesoría fiscal, administrador o servicios técnicos.',
  LEGAL_DEFENSE: 'Abogado, procurador, reclamaciones o defensa jurídica.',
  RENTAL_MANAGEMENT: 'Property management, comercialización o gestión del alquiler.',
  CLEANING: 'Limpieza, portería o servicios recurrentes similares.',
  BAD_DEBT: 'Impagos o saldos de dudoso cobro.',
  DEPRECIATION_PROPERTY: 'Amortización fiscal del inmueble.',
  DEPRECIATION_FURNITURE: 'Amortización de muebles, electrodomésticos y enseres.',
  ACQUISITION_COSTS: 'Compra, ITP/IVA, notaría, registro, agencia o tasación.',
  SALE_COSTS: 'Gastos vinculados a la transmisión del inmueble.',
  OTHER_DEDUCTIBLE_EXPENSE: 'Gasto necesario que no encaja mejor en otra categoría.',
  NON_DEDUCTIBLE_EXPENSE: 'Gasto claramente no deducible.',
  REVIEW: 'Movimiento ambiguo o pendiente de desglose.',
};

const typeColor = (type: TransactionType) => (type === 'INCOME' ? 'success.main' : 'error.main');
const typeTint = (type: TransactionType) => (type === 'INCOME' ? 'success.50' : 'error.50');
const typeLabel = (type: TransactionType) => (type === 'INCOME' ? 'Ingreso' : 'Gasto');

const filterOptions = createFilterOptions<CategoryOption>({
  trim: true,
  stringify: (option) => option.keywords,
});

function buildOptions(
  scope: CategoryScope,
  type: TransactionType | undefined,
  preferredType: TransactionType | undefined
): CategoryOption[] {
  const visibleGroups =
    scope === 'matching-type' && type
      ? CATEGORY_GROUPS.filter((group) => group.type === type)
      : CATEGORY_GROUPS;

  const orderedGroups = preferredType
    ? [...visibleGroups].sort((a, b) => {
        if (a.type === preferredType && b.type !== preferredType) return -1;
        if (a.type !== preferredType && b.type === preferredType) return 1;
        return 0;
      })
    : visibleGroups;

  return orderedGroups.flatMap((group) =>
    group.categories.map((category) => {
      const label = transactionCategoryLabels[category];
      const description = CATEGORY_DESCRIPTIONS[category];
      return {
        category,
        label,
        description,
        groupId: group.id,
        groupLabel: group.label,
        type: group.type,
        keywords: `${label} ${description} ${group.label} ${typeLabel(group.type)}`,
      };
    })
  );
}

export function TransactionCategorySelect({
  value = '',
  onChange,
  type,
  preferredType,
  scope = 'matching-type',
  label = 'Categoría',
  size = 'medium',
  fullWidth = true,
  disabled = false,
  error = false,
  helperText,
  includeAllOption = false,
  allOptionLabel = 'Todas',
  hideLabel = false,
  sx,
  selectSx,
}: TransactionCategorySelectProps) {
  const labelId = useId();
  const options = useMemo(
    () => buildOptions(scope, type, preferredType),
    [preferredType, scope, type]
  );

  const groupMeta = useMemo(() => {
    const map = new Map<string, { label: string; type: TransactionType }>();
    options.forEach((option) => {
      if (!map.has(option.groupId)) {
        map.set(option.groupId, { label: option.groupLabel, type: option.type });
      }
    });
    return map;
  }, [options]);

  const selectedOption = useMemo(
    () => (value ? (options.find((option) => option.category === value) ?? null) : null),
    [options, value]
  );

  const placeholder = includeAllOption ? allOptionLabel : 'Busca o selecciona una categoría';

  return (
    <Autocomplete<CategoryOption, false, boolean, false>
      options={options}
      value={selectedOption}
      onChange={(_event, next) => onChange(next ? next.category : '')}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      autoHighlight
      blurOnSelect
      disableClearable={!includeAllOption}
      filterOptions={filterOptions}
      groupBy={(option) => option.groupId}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.category === selected.category}
      noOptionsText="Sin coincidencias"
      sx={sx}
      slotProps={{
        popper: {
          sx: { minWidth: 'min(340px, calc(100vw - 32px))' },
        },
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: 2.5,
            boxShadow: '0 12px 40px -12px rgba(16, 24, 40, 0.28)',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
      ListboxProps={{
        sx: {
          maxHeight: 360,
          p: 0.5,
          overscrollBehavior: 'contain',
          [`& .${autocompleteClasses.option}`]: {
            borderRadius: 1.5,
            alignItems: 'flex-start',
            gap: 1.25,
            py: 0.875,
            px: 1.25,
          },
        },
      }}
      renderGroup={(params) => {
        const meta = groupMeta.get(params.group);
        if (!meta) return params.children;
        return (
          <li key={params.key}>
            <Box
              sx={{
                position: 'sticky',
                top: -4,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                pt: 1.25,
                pb: 0.75,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: typeColor(meta.type),
                }}
              />
              <Typography
                variant="overline"
                sx={{ lineHeight: 1.4, fontWeight: 700, letterSpacing: 0.6, color: 'text.primary' }}
              >
                {meta.label}
              </Typography>
            </Box>
            <Box component="ul" sx={{ p: 0, m: 0 }}>
              {params.children}
            </Box>
          </li>
        );
      }}
      renderOption={(props, option, { selected }) => {
        // eslint-disable-next-line react/prop-types
        const { key, ...liProps } = props as typeof props & { key: string };
        return (
          <Box
            component="li"
            key={key}
            {...liProps}
            sx={{
              position: 'relative',
              ...(selected && { bgcolor: typeTint(option.type) }),
              [`&.${autocompleteClasses.option}.Mui-focused`]: {
                bgcolor: selected ? typeTint(option.type) : 'action.hover',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 4,
                top: 8,
                bottom: 8,
                width: 3,
                borderRadius: 3,
                bgcolor: typeColor(option.type),
                opacity: selected ? 1 : 0,
                transition: 'opacity 120ms ease',
              },
              '&:hover::before': { opacity: 0.5 },
            }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1, pl: 0.75 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.35 }}
              >
                {option.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}
              >
                {option.description}
              </Typography>
            </Box>
            <CheckRoundedIcon
              fontSize="small"
              sx={{
                mt: 0.25,
                flexShrink: 0,
                color: typeColor(option.type),
                opacity: selected ? 1 : 0,
              }}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            'aria-label': hideLabel ? label : undefined,
          }}
          InputLabelProps={{ ...params.InputLabelProps, id: labelId }}
          sx={selectSx}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.25 }}>
                {selectedOption ? (
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: typeColor(selectedOption.type),
                      boxShadow: (t) =>
                        `0 0 0 3px ${
                          selectedOption.type === 'INCOME'
                            ? t.palette.success['50']
                            : (t.palette.error['50'] ?? t.palette.action.hover)
                        }`,
                    }}
                  />
                ) : (
                  <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}
